// ImageUpload.js
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Box, Button, Image, Text, FileInput, Layer, Header, Grommet } from 'grommet';
import { FormTrash, FormClose, Camera } from "grommet-icons";
import { useFetch } from "use-http";
import { getDBURL, updateStateHandler } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import { UserContext } from "../../App";
import DangerPopupWindow from "../Base components/DangerPopupWindow";
import WarningPopupWindow from "../Base components/WarningPopupWindow";
import { useErrorLogger } from './Logger';


const customTheme = {
    global: {
        colors: {
            brand: '#7D4CDB',
            text: '#7D4CDB',
            border: '#7D4CDB',
        },
        font: {
            family: 'Helvetica',
            size: '18px',
            height: '20px',
        },
    },
};

const ImageUpload = (props) => {
    const currentUser = useContext(UserContext);
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [error, setError] = useState('');
    const [showFileInput, setShowFileInput] = useState(true);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const { get, post } = useFetch(getDBURL());
    const { DBlogError } = useErrorLogger();

    const [dangerPopupOpen, setDangerPopupOpen] = useState(false); //show/hide Danger Popup Window
    const [dangerMessage, setDangerMessage] = useState('');
    const [dangerImage, setDangerImage] = useState('');
    const [dangerFuncOnConfirm, setDangerFuncOnConfirm] = useState('');

    const [showWarning, setShowWarning] = useState(false);

    // console.log(' props in ImageUpload component: ', props); 

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open

    // opens a popup window when the delete" button is clicked
    const openDangerPopupWindow = (msg, func) => {
        setDangerMessage(msg);
        setDangerFuncOnConfirm(() => func); // Store the callback function without executing it
        setDangerPopupOpen(true);
    }

    // hides the "Danger" popup window 
    const onDangerPopupClose = async () => {
        setImage(null);
        setShowFileInput(false);
        setTimeout(() => setShowFileInput(true), 0); // Reset File Input
        await getImage();
        setDangerPopupOpen(false);
        setDangerMessage(''); 
    };
    
    // fires when the "Yes" button is clicked in the "Danger" popup
    const onDangerConfirm = () => {
        // console.log(' << in function onDangerConfirm >> '); 
        if (typeof dangerFuncOnConfirm === 'function') {
            dangerFuncOnConfirm();
        } 
        onDangerPopupClose();
        setDangerFuncOnConfirm('');
    };

    const closeWarning = () => {
        setShowWarning(false);
    }

    /**Description:
    Fetches the actual image file (as a blob) directly from the server 
    and creates a preview URL using URL.createObjectURL.
    🟢  This method is more efficient, performing everything in one request 
    without needing the filename, ideal when you just want to load and display the image. */
    const getImage = async () => {
        if (props.initValue !== 1) return;

        try {
            const timestamp = Date.now();
            const url = props.imgType
                ? `${getDBURL()}/getImg/${props.docType}/${props.imgType}/${props.docId}?dt=${timestamp}`
                : `${getDBURL()}/getImg/${props.docType}/${props.docId}?dt=${timestamp}`;


            // Use native fetch because it can handle image blob responses
            const response = await fetch(url);
            if (!response.ok) {
                console.error('Failed to fetch image:', response.statusText);
                return;
            }

            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);

            setPreview(imageUrl);
            setDangerImage(imageUrl);

            if (props.updateImage !== undefined) {
                props.updateImage(imageUrl);
            }

        } catch (error) {
            console.error('Error fetching image:', error);

            await DBlogError({
                error,
                parameters: { docType: props.docType, imgType: props.imgType, docId: props.docId },
                userName: currentUser.email,
                moduleName: 'ImageUpload',
                functionName: 'getImage',
            });
        }
    };

    const updateImageExistDBField = async (newVal) => {
        // console.log(' << props.tableName in function updateImageExistDBField >> ', props.tableName); 
        // console.log(' << props.fieldName in function updateImageExistDBField >> ', props.fieldName); 
        // console.log(' << newVal in function updateImageExistDBField >> ', newVal); 

        if (props.initValue === newVal)
            return;
        
        try {
            const timestamp = Date.now();
            const response = await post(`/updateImageExistField?timestamp=${timestamp}`, {
                TableName: props.tableName,
                FieldName: props.fieldName,
                NewValue: newVal,
                Id: props.docId
            });

            if (!response.success) {
                throw new Error(`${response.message}`);
            }
        
            // Update  ImgExist field
            updateStateHandler(props.stateHandler, props.fieldName, newVal);

        } catch (error) {
            console.error('🔴 Error uploading the image:', error.message);
            updateImageExistDBField(null);
            // Construct error object
            await DBlogError({
                error: error,
                parameters: { docType: props.docType, imgType: props.imgType, docId: props.docId, fieldName: props.fieldName, newVal: newVal },
                userName: currentUser.email,
                moduleName: 'ImageUpload',
                functionName: 'updateImageExistDBField',
            });
        }
    };

    useEffect(() => {
        if (props.initValue === 1)
            getImage();
        else
            setPreview('BgPic.png');
            setDangerImage('');
    }, [props.initValue]);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            videoRef.current.srcObject = stream;
        } catch (err) {
            setError('Error accessing camera: ' + err.message);
        }
    };

    const capturePhoto = () => {
        const context = canvasRef.current.getContext('2d');
        if (!videoRef.current) return;
        
        // Set the canvas dimensions to match the video stream
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
    
        context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasRef.current.toBlob((blob) => {
            const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setImage(file);
    
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }, 'image/jpeg');
    };
    

    const handleFileChange = async (event) => {
        setUploadStatus('');
        setError('');
        setImage(null);
        setPreview('');

        const file = event.target.files[0];
        if (!file) {
            if (props.initValue === 1)
                await getImage();
            else {
                setPreview('BgPic.png');
                setDangerImage('');
            }
            return;
        }

        // Check file type
        if (!file.type.includes('image/jpeg') && !file.type.includes('image/jpg') && !file.type.includes('image/png')) {
            setError('Invalid file type. \nPlease select a PNG, JPEG or JPG file.');

            if (props.initValue === 1)
                await getImage();
            else {
                setPreview('BgPic.png');
                setDangerImage('');
            }
            return;
        }

        if (file.size > (5 * 1024 * 1024)) {  // 5MB
            setError('File size exceeds the limit of 5MB. \nPlease upload a smaller image.');
        }

        // Proceed with file handling if valid
        setImage(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const deleteImage = async() => {
        // check if image exist
        if (props.initValue !== 1) {
            setError('No image available for deletion.');
            return;            
        }

        try {
            const timestamp = Date.now();    
            const url = props.imgType ? 
                `/deleteImg/${props.docType}/${props.imgType}/${props.docId}?timestamp=${timestamp}` : 
                `/deleteImg/${props.docType}/${props.docId}?timestamp=${timestamp}`;
            // console.log('In deleteImage url >> ', url);

            const response = await post(url);   
            if (!response.success) {
                throw new Error(`${response.message}`);
            }           

            updateImageExistDBField(null);
            setUploadStatus(response.message);
            setImage(null);
            setPreview('BgPic.png');
            setDangerImage('');
            
            if (props.updateImage !== undefined)
                props.updateImage('BgPic.png');  
                
        } catch (error) {
            console.error('🔴 Error deleting the image:', error.message);
           
            // Construct error object
            await DBlogError({
                error: error,
                parameters: { docType: props.docType, imgType: props.imgType, docId: props.docId },
                userName: currentUser.email,
                moduleName: 'ImageUpload',
                functionName: 'deleteImage',
                description: `File ${props.docId} not found`
            });
            
            if (props.initValue === 1) {
                await getImage();
            }
            setError('Image deletion failed due to:\n' + error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setUploadStatus('');
        setImage("");

    
        if (!image) {
            setError('Please capture or select an image first');
            return;
        }

        const formData = new FormData();
        formData.append('image', image);
    
        try {
            const timestamp = Date.now();
            const url = props.imgType ? 
                `/uploadImg/${props.docType}/${props.imgType}/${props.docId}?timestamp=${timestamp}` : 
                `/uploadImg/${props.docType}/${props.docId}?timestamp=${timestamp}`;

            // console.log('⚠️ url in upload image >>>>>', url);
            const response = await post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
    
            if (!response.success) {
                throw new Error(`${response.message}`);
            }
    
            updateImageExistDBField(1);
            setUploadStatus(response.message);
            await getImage();

        } catch (error) {
            console.error('🔴 Error uploading the image:', error.message);
            updateImageExistDBField(null);
            // Construct error object
            await DBlogError({
                error: error,
                parameters: { docType: props.docType, imgType: props.imgType, docId: props.docId, filename: image.name },
                userName: currentUser.email,
                moduleName: 'ImageUpload',
                functionName: 'handleSubmit',
                description: `Error in uploading the image for ${image.name}`
            });
            setError('Image upload failed due to:\n' + error.message);
        }
    };

    return (
        <Grommet theme={customTheme}>
            <Layer 
                id="upload-image" 
                ref={modalRef}
                tabIndex="-1"
                position="center"            
            >
                <Box pad="xxsmall" margin="small" gap="small" width="420px" maxWidth="420px" background="#f2edfb" border>
                    <Box style={{ position: "relative" }}>
                    <Header margin="none" background="#e5dbf7" pad="none" height="xxsmall" justify="between" >
                            <Box direction="row" align="center" justify="center" fill="horizontal">
                                <Text alignSelf="center" size="large" color="brand">
                                    <strong>{props.disable ? 'View Photo' : 'Add or Change Photo'}</strong>
                                </Text>
                                <Box background="brand" style={{ position: "absolute", right: "2%",  }}>
                                    <Button 
                                        icon={<FormClose color='white'/>} 
                                        secondary 
                                        onClick={() => props.onClose(false)} 
                                        style={{ 
                                            paddingTop: '2px', 
                                            paddingBottom: '2px', 
                                            fontSize: '16px',
                                            width: '40px', 
                                            height: '40px', 
                                            display: 'flex',
                                            alignItems: 'center', 
                                            justifyContent: 'center'
                                        }} 
                                    />
                                </Box>
                            </Box>
                        </Header>

                        {/* <Box height="xsmall" width="xsmall" style={{ position: "absolute", right: "3%", top: "15px" }}>
                            <Image size="contain" src="Grape.png" />
                        </Box> */}
                    </Box>
                    
                    <Box gap="small" width="95%" margin="auto">
                        { !props.disable && showFileInput && (
                            <FileInput ref={fileInputRef} name="image" onChange={handleFileChange} pad="none" style={{ width: '100%' }} />
                        )}
                    </Box>

                    <Box 
                        align="start" 
                        height={error ? "50px" : (uploadStatus ? "20px" : "20px")} 
                        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', width: "95%", margin: "auto" }}
                    >
                        {(error || uploadStatus) && (
                            <Text 
                                color={error ? "status-critical" : "brand"} 
                                style={{ maxWidth: '100%', overflowWrap: 'break-word', whiteSpace: 'pre-line' }}
                            >
                                {error || uploadStatus}
                            </Text>
                        )}
                    </Box>

                    <Box align="center" height={error ? "270px" : "300px"}>
                        <canvas ref={canvasRef} style={{ display: 'none' }} width="320" height="240"></canvas>
                        {preview && (
                            <Box width="360px" height="300px" overflow="hidden" 
                                style={{
                                    // border: '2px solid #ccc',
                                 //   background: "white",
                                    margin: '10px',
                                    padding: '5px' }}>
                                <Image src={preview} alt="Preview" opacity={preview === 'BgPic.png' ? 'weak' : undefined} fit="contain" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                            </Box>
                        )}                         
                    </Box>
                    

                    <Box align="center" direction="row" gap="small" pad="small" justify='around'>
                    {!props.disable &&
                        <>
                            <Button 
                                icon={<Camera color='white'/>}
                                label={<Text color='white'>save</Text>} 
                                onClick={(event) => {
                                    if (error) {
                                        setShowWarning(true);}                        
                                    else if(props.initValue === 1 && image) {
                                        openDangerPopupWindow("The image will be changed to a new one! Are you sure you want to continue?", () => handleSubmit(event))
                                    } else {
                                        handleSubmit(event);
                                    }
                                }}
                                primary style={{ paddingTop: '2px', paddingBottom: '2px', fontSize: '16px' }} 
                            />

                    
                            <Button 
                                color="#801204"
                                icon={<FormTrash color='white'/>}
                                primary                        
                                label={<Text color='white'>delete</Text>} 
                                
                                onClick={() => {
                                    setError('');
                                    setUploadStatus('');
                                    {props.initValue === 1 ?
                                        (openDangerPopupWindow("The image will be deleted! Are you sure you want to continue?", deleteImage)
                                    ) : (
                                        setError('No image available for deletion.')                 
                                    )}}}
                            />
                        </>
                        }
                    </Box>
                </Box>
                {dangerPopupOpen && (
                <DangerPopupWindow
                    msg={dangerMessage}
                    imgSrc={dangerImage}
                    onClose={onDangerPopupClose}
                    onConfirm={onDangerConfirm}
                />
                )}

                {showWarning && (
                    <WarningPopupWindow msg={`The image can't be changed to new one due to:\n ${error}`} close={closeWarning}/>
                )}                               
            </Layer>
        </Grommet>
    );
};

export default ImageUpload;
